import { useNavigate, useParams } from "@tanstack/react-router"
import React, { useEffect, useState } from "react"
import toast, { Toaster } from "react-hot-toast"

import { trpc } from "../../lib/trpc"
import ApprovalWorkflow from "./ApprovalWorkflow"
import ContentAggregator from "./ContentAggregator"
import NewsletterPreview from "./NewsletterPreview"

type Tab = "content" | "editor" | "preview" | "approval"

interface Newsletter {
  id: string
  title: string
  subject?: string
  description?: string
  selectedSources: string[]
  dateRange?: { from: Date; to: Date }
  status: "draft" | "pending_approval" | "approved" | "sent" | "failed"
  editorContent?: string
  aggregatedContent?: any[]
}

export default function NewsletterEditor() {
  const { id } = useParams({ from: "/newsletter/$id" })
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>("content")
  const [newsletter, setNewsletter] = useState<Newsletter | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Fetch newsletter data
  const { data, isLoading, error, refetch } = trpc.newsletter.getById.useQuery(id)

  // Update newsletter mutation
  const updateNewsletter = trpc.newsletter.update.useMutation({
    onSuccess: (updatedNewsletter) => {
      setNewsletter(updatedNewsletter as Newsletter)
      setHasUnsavedChanges(false)
      toast.success("Newsletter updated successfully!")
    },
    onError: (error) => {
      const errorMessage = error.message || "Unknown error occurred"
      toast.error(`Failed to update newsletter: ${errorMessage}`)
      console.error("Newsletter update error:", error)
    },
  })

  useEffect(() => {
    if (data) {
      setNewsletter(data as Newsletter)
    }
  }, [data])

  const handleSave = () => {
    if (!newsletter) return

    updateNewsletter.mutate({
      id: newsletter.id,
      title: newsletter.title,
      subject: newsletter.subject,
      description: newsletter.description,
      editorContent: newsletter.editorContent,
      selectedSources: newsletter.selectedSources,
      dateRange: newsletter.dateRange,
    })
  }

  const handleNewsletterChange = (updates: Partial<Newsletter>) => {
    if (!newsletter) return

    setNewsletter({ ...newsletter, ...updates })
    setHasUnsavedChanges(true)
  }

  const tabs = [
    { id: "content", name: "Content Sources", icon: "📊" },
    { id: "editor", name: "Editor", icon: "✏️" },
    { id: "preview", name: "Preview", icon: "👁️" },
    { id: "approval", name: "Approval", icon: "✅" },
  ]

  if (isLoading) {
    return (
      <div className="min-h-screen bg-foregroundx flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 ">Loading newsletter...</p>
        </div>
      </div>
    )
  }

  if (error || !newsletter) {
    return (
      <div className="min-h-screen bg-foregroundx flex items-center justify-center">
        <div className="text-center text-destructive">
          <p className="text-xl font-semibold">Error loading newsletter</p>
          <p className="mt-2">{error?.message || "Newsletter not found"}</p>
          <div className="mt-4 space-x-4">
            <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Try Again
            </button>
            <button
              onClick={() => navigate({ to: "/newsletter" })}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-foregroundx">
      {/* Header */}
      <div className="bg-background shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button onClick={() => navigate({ to: "/newsletter" })} className="text-gray-500 hover:text-primary">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div>
                  <h1 className="text-2xl font-bold ">{newsletter.title}</h1>
                  <div className="flex items-center space-x-4 mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        newsletter.status === "draft"
                          ? "bg-foregroundx text-gray-800"
                          : newsletter.status === "pending_approval"
                            ? "bg-yellow-100 text-yellow-800"
                            : newsletter.status === "approved"
                              ? "bg-green-100 text-green-800"
                              : newsletter.status === "sent"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-red-100 text-destructive"
                      }`}
                    >
                      {newsletter.status.replace("_", " ").toUpperCase()}
                    </span>
                    {hasUnsavedChanges && <span className="text-orange-600 text-sm">• Unsaved changes</span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {hasUnsavedChanges && (
                  <button
                    onClick={handleSave}
                    disabled={updateNewsletter.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {updateNewsletter.isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-background border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as Tab)}
                className={`${
                  activeTab === tab.id
                    ? "border-blue-500 "
                    : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300"
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "content" && (
          <ContentAggregator newsletter={newsletter} onNewsletterChange={handleNewsletterChange} />
        )}

        {activeTab === "editor" && (
          <NewsletterInnerEditor newsletter={newsletter} onNewsletterChange={handleNewsletterChange} />
        )}

        {activeTab === "preview" && <NewsletterPreview newsletter={newsletter} />}

        {activeTab === "approval" && <ApprovalWorkflow newsletter={newsletter} onStatusChange={() => refetch()} />}
      </div>
    </div>
  )
}

// Simple WYSIWYG Editor Component
function NewsletterInnerEditor({
  newsletter,
  onNewsletterChange,
}: {
  newsletter: Newsletter
  onNewsletterChange: (updates: Partial<Newsletter>) => void
}) {
  const [subject, setSubject] = useState(newsletter.subject || "")
  const [content, setContent] = useState(newsletter.editorContent || "")

  useEffect(() => {
    setSubject(newsletter.subject || "")
    setContent(newsletter.editorContent || "")
  }, [newsletter])

  const handleSubjectChange = (newSubject: string) => {
    setSubject(newSubject)
    onNewsletterChange({ subject: newSubject })
  }

  const handleContentChange = (newContent: string) => {
    setContent(newContent)
    onNewsletterChange({ editorContent: newContent })
  }

  return (
    <div className="space-y-6">
      <div className="bg-background rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold  mb-4">Email Content</h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-primary">
              Subject Line
            </label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => handleSubjectChange(e.target.value)}
              placeholder="Enter email subject..."
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-primary">
              Email Content
            </label>
            <textarea
              id="content"
              rows={20}
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Enter your newsletter content here... You can use HTML formatting."
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
            />
            <p className="mt-2 text-sm text-gray-500">
              You can use HTML tags for formatting. The aggregated content will be automatically inserted above this
              content.
            </p>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#333",
            color: "#fff",
          },
          success: {
            style: {
              background: "#10B981",
            },
          },
          error: {
            style: {
              background: "#EF4444",
            },
          },
        }}
      />
    </div>
  )
}
